/**
 * RiskCategoryCard.jsx
 * 
 * Display card for individual risk category
 * Shows risk score, TCM syndromes, and recommendations
 */

import React, { useState } from 'react';

const RiskCategoryCard = ({
    risk,
    expanded = false,
    onToggle = () => { }
}) => {
    const [isExpanded, setIsExpanded] = useState(expanded);

    if (!risk) return null;

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
        onToggle(!isExpanded);
    };

    const getLevelStyle = (level) => {
        const colors = {
            very_low: { bg: 'rgba(27, 94, 32, 0.2)', border: '#1B5E20', text: '#4CAF50' },
            low: { bg: 'rgba(76, 175, 80, 0.2)', border: '#4CAF50', text: '#81C784' },
            moderate: { bg: 'rgba(255, 193, 7, 0.2)', border: '#FFC107', text: '#FFD54F' },
            high: { bg: 'rgba(255, 152, 0, 0.2)', border: '#FF9800', text: '#FFB74D' },
            very_high: { bg: 'rgba(244, 67, 54, 0.2)', border: '#F44336', text: '#EF5350' }
        };
        return colors[level?.code] || colors.moderate;
    };

    const levelStyle = getLevelStyle(risk.level);

    return (
        <div
            className="risk-category-card"
            style={{
                ...styles.card,
                borderColor: risk.needs_attention ? levelStyle.border : 'var(--border-color, #374151)'
            }}
        >
            {/* Header - Always visible */}
            <div style={styles.header} onClick={handleToggle}>
                <div style={styles.iconContainer}>
                    <span style={styles.icon}>{risk.icon}</span>
                </div>

                <div style={styles.info}>
                    <div style={styles.name}>{risk.name_vi}</div>
                    <div style={styles.nameCn}>{risk.name_cn}</div>
                </div>

                <div style={styles.scoreContainer}>
                    <div style={{
                        ...styles.scoreCircle,
                        backgroundColor: levelStyle.bg,
                        borderColor: levelStyle.border
                    }}>
                        <span style={{ ...styles.scoreValue, color: levelStyle.text }}>
                            {risk.score}
                        </span>
                    </div>
                    <span style={{ ...styles.levelLabel, color: levelStyle.text }}>
                        {risk.level?.label || 'N/A'}
                    </span>
                </div>

                <div style={styles.chevron}>
                    {isExpanded ? '▼' : '▶'}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div style={styles.expandedContent}>
                    {/* Component Scores */}
                    {risk.component_scores && Object.keys(risk.component_scores).length > 0 && (
                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>Nguồn dữ liệu</div>
                            <div style={styles.componentList}>
                                {Object.entries(risk.component_scores).map(([source, score]) => (
                                    <div key={source} style={styles.componentItem}>
                                        <span style={styles.componentIcon}>{sourceIcons[source] || '📊'}</span>
                                        <span style={styles.componentName}>{sourceLabels[source] || source}</span>
                                        <div style={styles.componentBar}>
                                            <div style={{
                                                ...styles.componentProgress,
                                                width: `${score}%`,
                                                backgroundColor: getScoreColor(score)
                                            }} />
                                        </div>
                                        <span style={styles.componentScore}>{score}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TCM Syndromes */}
                    {risk.tcm_syndromes && risk.tcm_syndromes.length > 0 && (
                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>Chứng YHCT liên quan</div>
                            <div style={styles.syndromeList}>
                                {risk.tcm_syndromes.map((syndrome, idx) => (
                                    <div key={idx} style={styles.syndromeItem}>
                                        <span style={styles.syndromeName}>{syndrome.name_vi}</span>
                                        <span style={styles.syndromeCorr}>
                                            {Math.round((syndrome.correlation || 0.8) * 100)}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Acupoints */}
                    {risk.acupoints && risk.acupoints.length > 0 && (
                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>💆 Huyệt đạo khuyến nghị</div>
                            <div style={styles.acupointList}>
                                {risk.acupoints.slice(0, 4).map((point, idx) => (
                                    <span key={idx} style={styles.acupointTag}>{point}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Affected Organs */}
                    {risk.affected_organs && risk.affected_organs.length > 0 && (
                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>Cơ quan liên quan</div>
                            <div style={styles.organList}>
                                {risk.affected_organs.map((organ, idx) => (
                                    <span key={idx} style={styles.organTag}>
                                        {organLabels[organ] || organ}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const sourceIcons = {
    tongue: '👅',
    face: '👤',
    symptoms: '📋',
    pulse: '💓'
};

const sourceLabels = {
    tongue: 'Thiệt chẩn',
    face: 'Diện chẩn',
    symptoms: 'Triệu chứng',
    pulse: 'Mạch chẩn'
};

const organLabels = {
    heart: '❤️ Tâm',
    liver: '🟢 Can',
    spleen: '🟡 Tỳ',
    lung: '⚪ Phế',
    kidney: '🔵 Thận',
    stomach: '🟠 Vị'
};

const getScoreColor = (score) => {
    if (score >= 80) return '#F44336';
    if (score >= 60) return '#FF9800';
    if (score >= 40) return '#FFC107';
    return '#4CAF50';
};

const styles = {
    card: {
        background: 'var(--bg-secondary, #16213e)',
        borderRadius: '12px',
        border: '1px solid',
        overflow: 'hidden',
        marginBottom: '12px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        cursor: 'pointer'
    },
    iconContainer: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        background: 'var(--bg-tertiary, #1a1a2e)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    icon: {
        fontSize: '20px'
    },
    info: {
        flex: 1
    },
    name: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--text-color, #fff)'
    },
    nameCn: {
        fontSize: '11px',
        color: 'var(--text-muted, #9CA3AF)'
    },
    scoreContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px'
    },
    scoreCircle: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        border: '2px solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    scoreValue: {
        fontSize: '14px',
        fontWeight: '700'
    },
    levelLabel: {
        fontSize: '9px',
        fontWeight: '500'
    },
    chevron: {
        fontSize: '10px',
        color: 'var(--text-muted, #9CA3AF)',
        marginLeft: '8px'
    },
    expandedContent: {
        padding: '0 16px 16px',
        borderTop: '1px solid var(--border-color, #374151)'
    },
    section: {
        marginTop: '12px'
    },
    sectionTitle: {
        fontSize: '11px',
        fontWeight: '600',
        color: 'var(--text-muted, #9CA3AF)',
        marginBottom: '8px'
    },
    componentList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    componentItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px'
    },
    componentIcon: {
        fontSize: '14px'
    },
    componentName: {
        width: '70px',
        color: 'var(--text-color, #fff)'
    },
    componentBar: {
        flex: 1,
        height: '4px',
        background: 'var(--bg-tertiary, #1a1a2e)',
        borderRadius: '2px',
        overflow: 'hidden'
    },
    componentProgress: {
        height: '100%',
        borderRadius: '2px'
    },
    componentScore: {
        width: '35px',
        textAlign: 'right',
        color: 'var(--text-muted, #9CA3AF)'
    },
    syndromeList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    syndromeItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 10px',
        background: 'var(--bg-tertiary, #1a1a2e)',
        borderRadius: '6px',
        fontSize: '12px'
    },
    syndromeName: {
        color: 'var(--accent-color, #4ECDC4)'
    },
    syndromeCorr: {
        color: 'var(--text-muted, #9CA3AF)'
    },
    acupointList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px'
    },
    acupointTag: {
        padding: '4px 10px',
        background: 'rgba(103, 58, 183, 0.2)',
        color: '#B39DDB',
        borderRadius: '12px',
        fontSize: '11px'
    },
    organList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    organTag: {
        fontSize: '12px',
        color: 'var(--text-color, #fff)'
    }
};

export default RiskCategoryCard;
